import { ChangeDetectionStrategy, Component, computed, effect, inject, signal, Signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { MatListItemIcon, MatNavList } from '@angular/material/list';
import { MatIconModule } from '@angular/material/icon';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { I18n, TranslatePipe } from '@corporate/ng-tebis-base-components';
import { MatButtonModule } from '@angular/material/button';
import { map } from 'rxjs';
import packageJson from '../../../../../../../package.json';
import cloudComponentsPackageJson from '@corporate/ng-tebis-base-components/package.json';
import { environment } from '../../../../environments/environment';
import { HttpClient } from '@angular/common/http';
import { MatMenuModule } from '@angular/material/menu';
import { AuthService } from '../../../core/services/auth/auth.service';
import { UserPreferencesService } from '../../../feature/user-preferences/user-preferences.service';
import { Language } from '../../../feature/user-preferences/user-preferences.model';
import { authorizeHttp } from '../../../core/util/authorize-http';
import { InitialsDisplayComponent } from '../../../ui/initials-display/initials-display.component';
import { MainMenuService } from './main-menu.service';
import { MatDialog } from '@angular/material/dialog';
import { HelpPageComponent } from '../../../feature/help-page/help-page.component';

@Component({
  selector: 'app-main-menu',
  imports: [
    MatNavList,
    MatIconModule,
    MatListItemIcon,
    MatButtonModule,
    RouterLink,
    TranslatePipe,
    MatMenuModule,
    InitialsDisplayComponent,
  ],
  templateUrl: './main-menu.component.html',
  styleUrl: './main-menu.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MainMenuComponent {

  private route = inject<ActivatedRoute>(ActivatedRoute);

  matDialog = inject(MatDialog);
  authService = inject(AuthService);
  i18n = inject(I18n);

  userPrefService = inject(UserPreferencesService);
  userPrefs = this.userPrefService.userPreferences;

  selected: Signal<string> = toSignal(this.route.data.pipe(map(data => data['selectedNavbarItem'])), {initialValue: ''});

  backendVersion = signal('');
  backendRevision = signal('');
  backendBranch = signal('');

  // show version with environment name 
  versionInfo = computed(() => this.state.expanded() ? `tebitasks v${packageJson.version}-${environment.name}` : packageJson.version);
  cloudComponentsVersionInfo = computed(() => this.state.expanded() ? `using cloud components v${cloudComponentsPackageJson.version}` : cloudComponentsPackageJson.version);
  backendVersionInfo = computed(() => this.state.expanded() ? `rds postgres backend v${this.backendVersion()} ${this.backendBranch()}@${this.backendRevision()}` : this.backendVersion());

  state = inject(MainMenuService);

  constructor() {
    authorizeHttp(inject(HttpClient), this.authService.accessTokenReadOnly).get(`${environment.baseUrlRds}/info`).subscribe(data => {
      if (data && typeof data === 'object') {
        if ('Version' in data) {
          this.backendVersion.set(String(data.Version));
        }
        if ('Revision' in data) {
          this.backendRevision.set(String(data.Revision));
        }
        if ('Branch' in data) {
          this.backendRevision.set(String(data.Branch));
        }
      }
    });

    effect(() => {
      if (this.userPrefs()) {
        this.i18n.setLang(this.userPrefs()!.language);
      }
    });

    effect(() => {
      if (this.authService.loggedIn()) {
        if (this.state.helpPageAutostart && !this.state.wasHelpPageOpened) {
          this.openHelpDialog();
          this.state.wasHelpPageOpened = true;
        }
      }
    });
  }

  logout() {
    this.authService.logout();
  }

  setLang(language: Language) {
    if (this.userPrefs()) {
      this.userPrefService.updateUserPreferences({...this.userPrefs()!, language});
    } else {
      this.i18n.setLang(language);
    }
  }

  openHelpDialog() {
    this.matDialog.open(HelpPageComponent, {
      width: '1200px',
      height: '1050px',
    });
  }
}
